import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { ErrorToast } from "../ToastComponent/RectHotToast";
import EditableTask from "./EditableTask";
import NewTask from "./NewTask";
import "./TodoItem.scss";

const CREATE_TODO_MUTATION = gql`
  mutation createTask($input: TaskInput) {
    createTask(input: $input) {
      _id
      Title
      Notes
      Priority
      Due_Date
      Is_Completed
      Assignee {
        _id
      }
    }
  }
`;

const UPDATE_TASK_MUTATION = gql`
  mutation UpdateTask($id: ID!, $input: TaskInput!) {
    updateTask(_id: $id, input: $input) {
      _id
      Title
      Notes
      Priority
      Due_Date
      Is_Completed
      Assignee {
        _id
        UserName
        Full_Name
      }
    }
  }
`;

const GET_ALL_TASKS = gql`
  query GetAllTasks {
    getAllTasks {
      completedtask {
        _id
        Title
        Notes
        Priority
        Due_Date
        createdAt
        updatedAt
        Is_Completed
        Is_Expanded
        Assignee {
          _id
          UserName
          Full_Name
        }
      }
      notCompletedTask {
        _id
        Title
        Notes
        Priority
        Due_Date
        createdAt
        updatedAt
        Is_Completed
        Is_Expanded
        Assignee {
          _id
          UserName
          Full_Name
        }
      }
    }
  }
`;

const USERS_QUERY = gql`
  query UsersSearch($keyword: String) {
    UsersSearch(keyword: $keyword) {
      _id
      UserName
      Full_Name
    }
  }
`;

const DELETE_TODO_MUTATION = gql`
  mutation DeleteTask($id: ID!) {
    deleteTask(_id: $id) {
      success
      message
    }
  }
`;

function Todo() {
  const [tasks, setTasks] = useState({ completedtask: [], notCompletedTask: [] });
  const [showCompleted, setShowCompleted] = useState(false);

  const Form_Key = "TaskForm";
  const FormDataFromRedux = useSelector((store) => store?.FormDataReducer);

  const [createTask] = useMutation(CREATE_TODO_MUTATION, {
    onCompleted: (data) => {
      if (data && data.createTask) {
        setTasks((prevTasks) => ({
          ...prevTasks,
          notCompletedTask: [...prevTasks.notCompletedTask, { ...data.createTask, Is_Expanded: false }],
        }));
      }
    },
    onError: (err) => {
      console.error("Error creating task:", err.message);
      ErrorToast("Failed to create task: " + err.message);
    },
  });

  const [updateTaskMutation, { loading: updateLoading }] = useMutation(UPDATE_TASK_MUTATION);

  console.log(updateLoading, "updateLoading");
  const [deleteTask, { loading: deleteLoading }] = useMutation(DELETE_TODO_MUTATION);

  const [fetchAllTasks] = useLazyQuery(GET_ALL_TASKS, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data && data.getAllTasks) {
        setTasks({
          completedtask: data.getAllTasks.completedtask.map((task) => ({
            ...task,
            Is_Expanded: false,
          })),
          notCompletedTask: data.getAllTasks.notCompletedTask.map((task) => ({
            ...task,
            Is_Expanded: false,
          })),
        });
      }
    },
    onError: (err) => {
      console.error("Error fetching tasks:", err.message);
    },
  });

  const { data: usersData } = useQuery(USERS_QUERY, {
    variables: { keyword: "" },
    onError: (err) => {
      console.error("Error fetching users:", err.message);
    },
  });

  useEffect(() => {
    fetchAllTasks();
  }, [fetchAllTasks]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const value = e.target.value;
      if (value.trim()) {
        const newTask = {
          Title: value,
          Notes: "",
          Due_Date: "",
          Priority: "NONE",
          Is_Completed: false,
          Assignee: null,
        };

        createTask({
          variables: { input: newTask },
        });
      }
    }
  };

  const handleCompletionToggle = (id, isCompleted) => {
    const taskToSave = tasks.notCompletedTask.concat(tasks.completedtask).find((task) => task._id === id);
    if (!taskToSave) {
      console.error("Task not found!");
      return;
    }
    const updatedTask = {
      ...taskToSave,
      Is_Completed: isCompleted,
    };

    const input = {
      Title: updatedTask.Title,
      Notes: updatedTask.Notes,
      Priority: updatedTask.Priority,
      Due_Date: updatedTask.Due_Date,
      Is_Completed: updatedTask.Is_Completed,
      Assignee: updatedTask.Assignee._id,
    };

    updateTaskMutation({
      variables: {
        id: updatedTask._id,
        input,
      },
    })
      .then((response) => {
        console.log("Task update successful:", response);
      })
      .catch((error) => {
        console.error("Error updating task:", error);
      });

    setTasks((prevTasks) => {
      let newCompleted = [...prevTasks.completedtask];
      let newNotCompleted = [...prevTasks.notCompletedTask];
      if (isCompleted) {
        newNotCompleted = newNotCompleted.filter((task) => task._id !== id);
        newCompleted.push(updatedTask);
      } else {
        newCompleted = newCompleted.filter((task) => task._id !== id);
        newNotCompleted.push(updatedTask);
      }
      return { completedtask: newCompleted, notCompletedTask: newNotCompleted };
    });
  };

  const onSave = (id) => {
    const taskToSave = tasks.notCompletedTask.concat(tasks.completedtask).find((task) => task._id === id);

    if (!taskToSave) {
      console.error("Task not found!");
      return;
    }

    const { __typename, createdAt, updatedAt, Is_Expanded, ...input } = taskToSave;

    delete input.Assignee;
    delete input._id;

    console.log("Final input sent to mutation:", input);
    const Assignee = FormDataFromRedux?.MasterData[Form_Key]?.Assignee?._id;
    input.Assignee = Assignee;
    updateTaskMutation({
      variables: {
        id: taskToSave._id,
        input,
      },
    })
      .then((response) => {
        console.log("Update success:", response);
      })
      .catch((error) => {
        console.error("Update error:", error);
      });
  };

  const onFieldUpdate = (id, field, value) => {
    if (field === "Is_Completed") {
      handleCompletionToggle(id, value);
    } else {
      setTasks((prevTasks) => {
        return {
          completedtask: prevTasks.completedtask.map((task) => (task._id === id ? { ...task, [field]: value } : task)),
          notCompletedTask: prevTasks.notCompletedTask.map((task) =>
            task._id === id ? { ...task, [field]: value } : task
          ),
        };
      });
    }
  };

  const deleteTaskHandler = (id) => {
    deleteTask({
      variables: { id },
      onCompleted: (data) => {
        if (data.deleteTask.success) {
          setTasks((prevTasks) => {
            const notCompletedTask = prevTasks.notCompletedTask.filter((task) => task._id !== id);
            const completedtask = prevTasks.completedtask.filter((task) => task._id !== id);
            return { notCompletedTask, completedtask };
          });
        } else {
          ErrorToast(data.deleteTask.message || "Error deleting task");
        }
      },
      onError: (err) => {
        console.error("Error deleting task:", err.message);
        ErrorToast("Failed to delete task");
      },
    });
  };

  const toggleExpand = (id, isCompleted) => {
    setTasks((prev) => {
      const taskType = isCompleted ? "completedtask" : "notCompletedTask";
      return {
        ...prev,
        [taskType]: prev[taskType].map((task) =>
          task._id === id ? { ...task, Is_Expanded: !task.Is_Expanded } : { ...task, Is_Expanded: false }
        ),
      };
    });
  };


  return (
    <div className="todo-list-container">
      <div className="todo-header">
        <h2 className="todo-title">Todo List</h2>
        <div className="task-count">Incompleted Tasks: {tasks.notCompletedTask.length}</div>
      </div>
      {tasks.notCompletedTask.map((task) => (
        <EditableTask
          key={task._id}
          task={task}
          {...task}
          assignees={usersData?.UsersSearch || []}
          onFieldUpdate={onFieldUpdate}
          onToggleExpand={() => toggleExpand(task._id, false)}
          onSave={onSave}
          onDelete={() => deleteTaskHandler(task._id)}
          updateLoading={updateLoading}
          deleteLoading={deleteLoading}
          Form_Key={Form_Key}
        />
      ))}

      {showCompleted &&
        tasks.completedtask.map((task) => (
          <EditableTask
            key={task._id}
            task={task}
            {...task}
            assignees={usersData?.UsersSearch || []}
            onFieldUpdate={onFieldUpdate}
            onToggleExpand={() => toggleExpand(task._id, true)}
            onSave={onSave}
            onDelete={() => deleteTaskHandler(task._id)}
            updateLoading={updateLoading}
            deleteLoading={deleteLoading}
            Form_Key={Form_Key}
          />
        ))}

      <div className="center-button">
        <button className="done-button" onClick={() => setShowCompleted(!showCompleted)}>
          {showCompleted
            ? `Hide Completed (${tasks.completedtask.length})`
            : `Show Completed (${tasks.completedtask.length})`}
        </button>
      </div>
      <NewTask handleKeyDown={handleKeyDown} />
    </div>
  );
}

export default Todo;
